import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {uuid} from 'uuidv4';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';

class MasterAmpEnvelope {

    constructor(sequencerEngine, mixSession, context, ampEnvelopes, instrument) {
        this.sequencerEngine = sequencerEngine;
        this.id = uuid();
        this.store = this.sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context.getCopy()
            .forSubDevice(this);

        this.ampEnvelopes = ampEnvelopes;

        this.parameters = [];
        this.setupParameters();
    }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    refresh() {
        this.attack = this.attack;
        this.decay = this.decay;
        this.sustain = this.sustain;
        this.release = this.release;
    }

    get attack() {
        return this.attackParameter.get();
    }

    get decay() {
        return this.decayParameter.get();
    }

    get sustain() {
        return this.sustainParameter.get();
    }

    get release() {
        return this.releaseParameter.get();
    }

    setupParameters() {
        this.attackParameter = new AbstractParameter(
            'Attack',
            this.envelope, (a, b) => this.ampEnvelopes.forEach(a => a.attack = b),
            {
                min: 0,
                max: 10000,
                unit: 'ms',
                defaultValue: 80,
            });

        this.registerParameter(this.attackParameter);

        this.decayParameter = new AbstractParameter(
            'Decay',
            this.envelope, (a, b) => this.ampEnvelopes.forEach(a => a.decay = b),
            {
                min: 0,
                max: 3000,
                unit: 'ms',
                defaultValue: 500
            });

        this.registerParameter(this.decayParameter);

        this.sustainParameter = new AbstractParameter(
            'Sustain',
            this.envelope, (a, b) => this.ampEnvelopes.forEach(a => a.sustain = b),
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.8
            });
            
        this.registerParameter(this.sustainParameter);

        this.releaseParameter = new AbstractParameter(
            'Release',
            this.envelope, (a, b) => this.ampEnvelopes.forEach(a => a.release = b),
            {
                min: 0,
                max: 3000,
                unit: 'ms',
                defaultValue: 100
            });

        this.registerParameter(this.releaseParameter);
    }

    get release() {
        return this.releaseParameter.get();
    }

    get decay() {
        return this.decayParameter.get();
    }

    get sustain() {
        return this.sustainParameter.get();
    }

    get attack() {
        return this.attackParameter.get();
    }

    set release(s) {
        this.releaseParameter.set(s, true, true);
    }

    set decay(s) {
        this.decayParameter.set(s, true, true);
    }

    set sustain(s) {
        this.sustainParameter.set(s, true, true);
    }

    set attack(s) {
        this.attackParameter.set(s, true, true);
    }

    getJson() {
        let json = {
            attack: this.attack,
            decay: this.decay,
            sustain: this.sustain,
            release: this.release,
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height
        };

        json = saveParameterToJson(json, 'attackParameter', this.attackParameter);
        json = saveParameterToJson(json, 'decayParameter', this.decayParameter);
        json = saveParameterToJson(json, 'sustainParameter', this.sustainParameter);
        json = saveParameterToJson(json, 'releaseParameter', this.releaseParameter);
        return json;
    }

    fromJson(json) {
        let bulk ={};

        this.x = json.x;
        this.y = json.y;
        this.width = json.width;
        this.height= json.height;

        bulkUpdate(this.attackParameter, json.attack, bulk);
        bulkUpdate(this.decayParameter, json.decay, bulk);
        bulkUpdate(this.sustainParameter, json.sustain, bulk);
        bulkUpdate(this.releaseParameter, json.release, bulk);

        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'attackParameter', this.attackParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'decayParameter', this.decayParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'sustainParameter', this.sustainParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'releaseParameter', this.releaseParameter, this.sequencerEngine, false, true)
        };
        return bulk;
    }

    dispose() {}
}

export default MasterAmpEnvelope;
